import React from 'react';
import { Handle, Position } from 'reactflow';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading5, Variant } from '~/components/atom/Typography';
import {
  FlowAction,
  type FlowV2_TemplateStringFragment,
} from '~/graphql/types';
import type { ClientFlowActionContactDetails } from '~/graphql/types.client';
import getCardHeading from '../../../../utils/getCardHeading';
import CardTemplate, { handleStyle, Props as CardProps } from '../CardTemplate';
import TemplateString from '../TemplateString';
import getActiveField from '../../../../../UpdateAction/components/ContactDetails/utils/getActiveField';
import { ADDRESS_SUBJECT } from '../../../../../UpdateAction/components/AddressField/constants';
import isTemplateString from '~/typeguards/isTemplateString';
import IssuesForAction from '../IssuesForAction';
import useRelativeMaps from '../../../../hooks/useRelativeMaps';
import isPointerArgument from '~/components/page/Automation/v2/util/isPointerArgument';
import getPathForPointer from '../../../../../UpdateAction/components/Selector/utils/getPathForPointer';
import {
  getPathRepresentation,
  text as labelText,
} from '../../../../../UpdateAction/components/ConditionEditorV2/utils/getLabelForRep';
import ActionLabelAsText from '../../../ActionLabelAsText';
import { Container as VariableContainer } from '../TemplateString/components/Variable';

export type Props = Omit<
  CardProps<ClientFlowActionContactDetails>,
  'heading' | 'actionType'
> & {
  dataTestId?: string;
};

const text = {
  name: 'Naam',
  phone: 'Telefoonnummer',
  unsubscribeEmail: 'Uitschrijven van e-mails',
  address: 'Adres',

  boolean: {
    true: 'Ja',
    false: 'Nee',
  },
};

const ContactDetailsCard: React.FCC<Props> = React.memo(
  ({ selected, disabled, data, ...props }) => {
    const { field } = data.action;

    const activeFieldKey = getActiveField(data.action);
    const activeFieldValue = field[activeFieldKey];

    return (
      <>
        <Handle
          type="target"
          position={Position.Top}
          style={handleStyle}
          isConnectable={false}
        />
        <CardTemplate
          {...props}
          actionType={FlowAction.ContactDetails}
          heading={getCardHeading(FlowAction.ContactDetails)}
          selected={selected}
          disabled={disabled}
          data={data}
          dataTestId="ContactDetailsCard"
        >
          <JustificationContainer direction="column">
            <WithMargins>
              <JustificationContainer align="center">
                <Heading5 margin={[null]} inline variant={Variant.primary}>
                  {text[activeFieldKey]}:&nbsp;
                </Heading5>
                {activeFieldKey === 'address' ? (
                  <AddressValue action={data.action} />
                ) : !isTemplateString(activeFieldValue) ? (
                  <div>{text.boolean[`${activeFieldValue as boolean}`]}</div>
                ) : (
                  <TemplateString
                    templateString={
                      activeFieldValue as FlowV2_TemplateStringFragment
                    }
                    actionId={data.action.id}
                  />
                )}
              </JustificationContainer>
            </WithMargins>

            <IssuesForAction actionId={data.action.id} />
          </JustificationContainer>
        </CardTemplate>
        <Handle
          type="source"
          position={Position.Bottom}
          style={handleStyle}
          isConnectable={false}
        />
      </>
    );
  },
);

/**
 * Shows the flow value the address is taken from, or an error when the pointer no longer resolves
 */
const AddressValue: React.FC<{ action: ClientFlowActionContactDetails }> = ({
  action,
}) => {
  const maps = useRelativeMaps({ actionId: action.id });
  const { address } = action.field;

  let addressLabel: string | null = null;
  if (address && isPointerArgument(address)) {
    const path = getPathForPointer(address.pointer, {
      subjectMap: maps.subjectMap,
      instanceMap: maps.instanceMap,
      directoryMap: maps.directoryMap,
      validTypes: [ADDRESS_SUBJECT],
    });

    if (path.error == null) {
      addressLabel = getPathRepresentation(path.fullResult);
    }
  }

  if (addressLabel) {
    return (
      <VariableContainer>
        <ActionLabelAsText str={addressLabel} highlightLabel />
      </VariableContainer>
    );
  }

  return (
    <VariableContainer $isValid={false}>
      {labelText.invalidVariable}
    </VariableContainer>
  );
};

const WithMargins = styled.div<{}>(
  ({ theme }) => css`
    margin-bottom: ${theme.space('s')};
  `,
);

export default ContactDetailsCard;
